/**
 * GrailScreen
 * Full-screen takeover for ultra rare + graded pulls.
 * Fires canvas-confetti, plays particle animation, renders the card
 * with maximum drama. Triggered by the parent after reveal.
 */
import { useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import type { LiveCard } from "../hooks/usePokemonTCG";
import { PokemonCard, RARITY_COLOR } from "./PokemonCard";
import "./GrailScreen.scss";

interface Props {
  card: LiveCard | null;
  onClose: () => void;
}

const TIER_COPY: Record<string, { title: string; sub: string }> = {
  graded:  { title: "GRADED SLAB 🏆", sub: "An authenticated, professionally graded collectible. You can have this shipped directly to you." },
  ultra:   { title: "ULTRA RARE 💎",  sub: "An incredible pull. One of the most sought-after cards in the game." },
  default: { title: "AMAZING PULL ✨", sub: "You hit something special. Congrats!" },
};

function firework(origin: { x: number; y: number }, colors: string[]) {
  confetti({
    particleCount: 120,
    startVelocity: 40,
    spread: 360,
    origin,
    colors,
    ticks: 250,
    gravity: 0.6,
    decay: 0.93,
    scalar: 1.1,
  });
}

export function GrailScreen({ card, onClose }: Props) {
  const fired = useRef(false);

  const fireConfetti = useCallback(() => {
    if (fired.current) return;
    fired.current = true;

    const isGraded = card?.rarity === "graded";
    const colors = isGraded
      ? ["#ffd700", "#ffb700", "#fffacd", "#ffffff", "#ffa500"]
      : ["#c084fc", "#818cf8", "#38bdf8", "#f472b6", "#ffffff"];

    // Triple burst
    setTimeout(() => firework({ x: 0.25, y: 0.5 }, colors), 0);
    setTimeout(() => firework({ x: 0.75, y: 0.4 }, colors), 200);
    setTimeout(() => firework({ x: 0.5,  y: 0.6 }, colors), 400);
    setTimeout(() => firework({ x: 0.15, y: 0.7 }, colors), 600);
    setTimeout(() => firework({ x: 0.85, y: 0.7 }, colors), 700);
  }, [card]);

  useEffect(() => {
    if (card) {
      fired.current = false;
      const timer = setTimeout(fireConfetti, 300);
      return () => clearTimeout(timer);
    }
  }, [card, fireConfetti]);

  const copy = TIER_COPY[card?.rarity ?? "default"] ?? TIER_COPY.default;
  const accentColor = card ? RARITY_COLOR[card.rarity] : "#c084fc";

  return (
    <AnimatePresence>
      {card && (
        <motion.div
          className="GrailScreen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          style={{ "--grail-color": accentColor } as React.CSSProperties}
        >
          {/* Radial bg */}
          <div className="GrailScreen__bg" />

          {/* Rotating rays */}
          <motion.div
            className="GrailScreen__rays"
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          />

          {/* Content */}
          <div className="GrailScreen__content" onClick={(e) => e.stopPropagation()}>
            {/* Title */}
            <motion.div
              className="GrailScreen__title"
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              {copy.title}
            </motion.div>

            {/* Card */}
            <motion.div
              className="GrailScreen__card-wrap"
              initial={{ scale: 0.3, opacity: 0, rotateY: -90 }}
              animate={{ scale: 1, opacity: 1, rotateY: 0 }}
              transition={{ delay: 0.3, duration: 0.8, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <PokemonCard card={card} index={0} revealed size="lg" />
            </motion.div>

            {/* Sub copy */}
            <motion.p
              className="GrailScreen__sub"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              {copy.sub}
            </motion.p>

            {/* Card detail */}
            <motion.div
              className="GrailScreen__card-info"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              <span className="GrailScreen__card-name">{card.name}</span>
              <span className="GrailScreen__card-set">{card.setName}</span>
              <span className="GrailScreen__card-val">
                ~${card.approxValueUsd >= 1000
                  ? (card.approxValueUsd / 1000).toFixed(1) + "k"
                  : card.approxValueUsd.toLocaleString()}
              </span>
            </motion.div>

            <motion.button
              className="GrailScreen__close"
              onClick={onClose}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              Continue to All Cards
            </motion.button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
